import {
  InterestRateType,
  LeasingObjectDetails,
  LeasingOfferDetails,
  LeasingType,
} from 'modules/types/LeasingOffer';
import i18next from 'i18next';
import { LeasingOffersPathsEnum } from '../leasingOffersPage/routes';

export enum LeasingOfferOverviewTab {
  OfferOverview = 'offer-overview',
  Success = 'success',
}

export enum LeasingDetailsKeys {
  BAIL = 'Jamčevina',
  TOTAL_COMPENSATION_AMOUNT = 'Iznos ukupne naknade',
  INSTALLMENT_AMOUNT = 'Iznos leasing rate',
  LEASING_ADMINISTRATIVE_EXPENSES = 'Administrativni troškovi', // 'Administartivni' - this is how it was implemented by UWT
  BUYOUT_VALUE = 'Otkupna vrijednost objekta leasinga',
  DEDUCTABLE = 'Učešće',
  ADVANCE_PAYMENT = 'Akontacija',
}

export const getLeasingOfferOverviewPageUrl = (
  offerNumber: string,
  tab: LeasingOfferOverviewTab,
) => `${LeasingOffersPathsEnum.leasingOffers}/${offerNumber}/${tab}`;

export const getPaymentTotalAmountByName = (
  name: string,
  leasingDetails?: LeasingObjectDetails[],
) =>
  leasingDetails?.find((leasingDetailItem) => leasingDetailItem.name === name)
    ?.amounts?.total;

export const getTranslatedNominalInterestRate = (interestRateType?: string) => {
  switch (interestRateType) {
    case InterestRateType.FIXED:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.nominalInterestRate',
      );
    case InterestRateType.VARIABLE:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.creationDateNominalInterestRate',
      );
    default:
      return '';
  }
};

export const getTranslatedParticipation = (leasingTypeName?: string) => {
  switch (leasingTypeName) {
    case LeasingType.OPERATIONAL:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.operationalLeasingParticipation',
      );
    case LeasingType.FINANCIAL:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.financialLeasingParticipation',
      );
    default:
      return leasingTypeName || '';
  }
};

export const getTranslatedTotalCompensationAmount = (
  leasingTypeName?: string,
  totalCompensationAmountLabel?: string,
) => {
  if (leasingTypeName === LeasingType.OPERATIONAL) {
    return i18next.t(
      'leasingOfferOverviewPage.offerOverviewTab.overviewTable.operationalLeasingTotalCompensationAmount',
      { totalCompensationAmountLabel },
    );
  }
  return totalCompensationAmountLabel || '';
};

export const getTranslatedInstallmentAmount = (leasingTypeName?: string) => {
  switch (leasingTypeName) {
    case LeasingType.OPERATIONAL:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.overviewTable.operationalLeasingInstallmentAmount',
      );
    case LeasingType.FINANCIAL:
      return i18next.t(
        'leasingOfferOverviewPage.offerOverviewTab.overviewTable.financialLeasingInstallmentAmount',
      );
    default:
      return leasingTypeName || '';
  }
};

export const getTranslatedLeasingDetailsName = (
  name?: string,
  leasingTypeName?: string,
  interestRateType?: string,
) => {
  if (name === LeasingDetailsKeys.TOTAL_COMPENSATION_AMOUNT) {
    return getTranslatedTotalCompensationAmount(leasingTypeName, name);
  }

  if (name === LeasingDetailsKeys.INSTALLMENT_AMOUNT) {
    const installmentAmountLabel =
      getTranslatedInstallmentAmount(leasingTypeName);

    if (interestRateType === InterestRateType.VARIABLE) {
      return `${installmentAmountLabel}*`;
    }
    return installmentAmountLabel;
  }

  if (name === LeasingDetailsKeys.LEASING_ADMINISTRATIVE_EXPENSES) {
    return i18next.t(
      'leasingOfferOverviewPage.offerOverviewTab.overviewTable.administrativeExpenses',
    );
  }

  return name;
};

export const getTotalAmountByLeasingType = (
  offerOverview?: LeasingOfferDetails,
) => {
  let totalAmount = offerOverview?.participationAmount || 0;
  totalAmount +=
    getPaymentTotalAmountByName(
      LeasingDetailsKeys.LEASING_ADMINISTRATIVE_EXPENSES,
      offerOverview?.leasingDetails,
    ) || 0;

  if (offerOverview?.leasingTypeName === LeasingType.OPERATIONAL) {
    totalAmount +=
      getPaymentTotalAmountByName(
        LeasingDetailsKeys.BAIL,
        offerOverview?.leasingDetails,
      ) || 0;
  }

  return totalAmount;
};

export const getOverallAmount = (
    offerOverview?: LeasingOfferDetails,
) => {
  let totalAmount = 0;
  totalAmount +=
      getPaymentTotalAmountByName(
          LeasingDetailsKeys.LEASING_ADMINISTRATIVE_EXPENSES,
          offerOverview?.leasingDetails,
      ) || 0;

  totalAmount +=
      getPaymentTotalAmountByName(
          LeasingDetailsKeys.DEDUCTABLE,
          offerOverview?.leasingDetails,
      ) || 0;

  totalAmount +=
      (offerOverview?.financingDuration || 1) * (
      getPaymentTotalAmountByName(
          LeasingDetailsKeys.INSTALLMENT_AMOUNT,
          offerOverview?.leasingDetails,
      ) || 0);

  totalAmount +=
      getPaymentTotalAmountByName(
          LeasingDetailsKeys.BUYOUT_VALUE,
          offerOverview?.leasingDetails,
      ) || 0;

  totalAmount += getPaymentTotalAmountByName(
      LeasingDetailsKeys.ADVANCE_PAYMENT,
      offerOverview?.leasingDetails,
  ) || 0;

  return totalAmount;
};
